import { SnackbarForPromise } from 'annotation-tools/ui-parts';
import {
  promiseState,
  useBooleanState,
  useDataStream,
  useNavigator,
  useStreamValue
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { switchMap } from 'rxjs/operators';
import styled from 'styled-components';
import { deleteProjectImage } from '../../../api/fetch-functions/project-image';
import { viewTexts } from '../../../constants/view-texts';
import { useSnackbar } from '../../../functions/hooks/use-snackbar';
import { useToken } from '../../../global-state/auth-state';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { mapListLatest } from '../../../global-state/database/common/map-latest';
import {
  fetchProjectImageListOf,
  projectImageListOf
} from '../../../global-state/database/project-image-list';
import { projectId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { routes } from '../../../routing/routes';
import { ICompanyType } from '../../../types/record/company';
import { IProjectImageInfoType } from '../../../types/record/image-info';
import { IProjectType } from '../../../types/record/project';
import { ConfirmDeleteDialogTemplate } from '../../dialogs/confirm-delete-dialog-template/confirm-delete-dialog-template';
import { snackbarMessages } from '../../rect-tool-with-crop/constants/messages/snackbar-message';
import { MessageArea } from '../../shared/message-area';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { ProjectImageListView } from './project-image-list-view';

const vt = viewTexts.dialogs.deleteProjectImage;

const MessageAreaWrapper = styled.div`
  padding: 20px;
`;

interface Props {
  koumutenId: number;
  company: ICompanyType;
  project: IProjectType;
}

export const ProjectImageList: FC<Props> = memo(
  ({ koumutenId, company, project }: Readonly<Props>) => {
    const navigator = useNavigator();

    const token = useToken();

    const imageList$ = useDataStream<
      ListWithPromiseState<IProjectImageInfoType>
    >(
      listWithPromiseStateInit<IProjectImageInfoType>(),
      projectId$.pipe(switchMap(projectImageListOf))
    );

    /* states */

    const [annotatedIsVisible, showAll, hideAnnotated] = useBooleanState(true);
    const [IsBulkDelete,InstantDelete,ConfirmDelete] = useBooleanState(false);

    const [
      confirmDeleteDialogIsOpen,
      openConfirmDeleteDialog,
      closeConfirmDeleteDialog
    ] = useBooleanState(false);

    const [imageIdToDelete, setImageIdToDelete] = useState<number | undefined>(
      undefined
    );

    const {
      snackbarIsOpen,
      openSnackbar,
      closeSnackbar,
      snackbarState,
      setSnackbarState,
      snackbarMessage,
      setSnackbarMessage
    } = useSnackbar();

    /* handlers */

    const deleteImage = useCallback(
      (imgId: number) => {
        if (
          token === undefined ||
          imgId === undefined
        ) {
          return;
        }

        setSnackbarState('pending');
        deleteProjectImage(token)(project.id, imgId).then(res => {
          openSnackbar();
  
          if (res.type === 'success') {
            fetchProjectImageListOf(project.id);
            setSnackbarState('success');
            setSnackbarMessage(snackbarMessages.delete.success);
          } else {
            console.error(res.body);
            setSnackbarState('error');
            setSnackbarMessage(snackbarMessages.delete.error);
          }
        });
      },
      [
        token,
        project.id,
        setSnackbarState,
        setSnackbarMessage,
        openSnackbar
      ]
    );

    const onImageClick = useCallback(
      (imgId: number) => {
        navigator(
          routes.projectImageRoot(koumutenId, company.id, project.id, imgId)
        );
      },
      [navigator, koumutenId, company.id, project.id]
    );

    const onDeleteClick = useCallback(
      (imgId: number) => {
        setImageIdToDelete(imgId);
        if(IsBulkDelete){
          deleteImage(imgId)
        }else{
          openConfirmDeleteDialog();
        }
      },
      [openConfirmDeleteDialog,deleteImage,IsBulkDelete]
    );

    const onConfirmDeleteDialogClose = useCallback(
      (yn: 'yes' | 'no') => {
        if (
          yn === 'no' ||
          imageIdToDelete === undefined
        ) {
          closeConfirmDeleteDialog();
          return;
        }
        deleteImage(imageIdToDelete)
        closeConfirmDeleteDialog()
      },
      [
        closeConfirmDeleteDialog,
        deleteImage,
        imageIdToDelete,
      ]
    );

    /* extract values */

    const imageList = useStreamValue(imageList$);

    const imageListFiltered = useMemo(
      () =>
        imageList === undefined
          ? undefined
          : mapListLatest(imageList, list =>
              annotatedIsVisible ? list : list.filter(v => !v.isAnnotated)
            ),
      [annotatedIsVisible, imageList]
    );

    useLoadingPage(
      imageList?.status === 'pending' || imageListFiltered?.status === 'pending'
    );

    return (
      <>
        {project.status === 'approved' ? (
          <MessageAreaWrapper>
            <MessageArea
              status='default'
              messages={
                viewTexts.pages.projectImageList.messages.readonlyProject
              }
            />
          </MessageAreaWrapper>
        ) : imageList === undefined || imageListFiltered === undefined ? (
          undefined
        ) : imageList.status === promiseState.error ||
          imageListFiltered.status === promiseState.error ? (
          <NotFoundTemplate
            message={viewTexts.notFoundMessages.modelImageList}
          />
        ) : (
          <>
            <ProjectImageListView
              imageList={imageList.value.latest}
              imageListFiltered={imageListFiltered.value.latest}
              annotatedIsVisible={annotatedIsVisible}
              showAll={showAll}
              hideAnnotated={hideAnnotated}
              onImageClick={onImageClick}
              onDeleteClick={onDeleteClick}
              IsBulkDelete={IsBulkDelete}
              InstantDelete={InstantDelete}
              ConfirmDelete={ConfirmDelete}
            />
            <ConfirmDeleteDialogTemplate
              title={vt.title}
              messages={vt.message}
              open={confirmDeleteDialogIsOpen}
              onClose={onConfirmDeleteDialogClose}
            />
            <SnackbarForPromise
              isOpen={snackbarIsOpen}
              message={snackbarMessage}
              promiseState={snackbarState}
              onCloseClick={closeSnackbar}
            />
          </>
        )}
      </>
    );
  }
);

ProjectImageList.displayName = 'ProjectImageList';
